import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { RunEnvironmentDiskSpaceResponseBody } from "common";
import { useImpersonationUser } from "@via/hooks/useImpersonationUser.tsx";

/**
 * The disk space check on a saved run environment (Directories card, run page Execution Directory). The result is
 * shown inline by `DiskSpaceCheck`, so no global notification is raised here. `asUserId` names whose access the
 * check uses, over the profile page's impersonation; the server honors either only for an admin.
 *
 * With `runId` the check goes to the run instead: the server checks the run's saved environment and execution
 * directory as its owner, so neither the environment, the path nor a user is sent.
 */
export const useCheckRunEnvironmentDiskSpace = (): UseMutationResult<
  RunEnvironmentDiskSpaceResponseBody,
  Error,
  {
    runEnvironmentId?: number | null;
    path?: string;
    measureFolder?: boolean;
    asUserId?: number | null;
    runId?: number | null;
  }
> => {
  const impersonationId = useImpersonationUser();
  return useMutation({
    mutationFn: async ({
      runEnvironmentId,
      path,
      measureFolder,
      asUserId,
      runId,
    }) => {
      if (runId) {
        const res = await axios.post(`/api/v1/run/${runId}/disk-space`, {
          ...(measureFolder && { measureFolder: true }),
        });
        return res.data as RunEnvironmentDiskSpaceResponseBody;
      }
      const userId = asUserId ?? impersonationId;
      const res = await axios.post(
        `/api/user/v1/profile-cluster/${runEnvironmentId}/disk-space`,
        {
          path,
          ...(measureFolder && { measureFolder: true }),
          ...(userId && { userId }),
        }
      );
      return res.data as RunEnvironmentDiskSpaceResponseBody;
    },
  });
};
